import React, { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import "./Home.scss";
import { Col, Container, Row } from "../../../components/Grid/Grid";
import { Typography } from "../../../components/__common/Typography/Typography";
import { Svg, SVG_ICONS } from "../../../components/__common/Svg/Svg";

import { ApplicationState } from "../../../store";
import { ROUTES } from "../../../configs/routes";
import triggerGAEvent from "../../../utils/UtilsTriggerGAEvent";
import {
  EVENT_CATEGORY_HOME_PAGE,
  EVENT_HOME_ACTION_LEARN_HOW,
  EVENT_HOME_ACTION_SEE_HOW,
  EVENT_HOME_ACTION_CLICK_HERE,
  EVENT_HOME_ACTION_SECURITY_PRIVACY,
  EVENT_HOME_ACTION_MANAGE_DATA,
  EVENT_HOME_ACTION_GPS,
  EVENT_CATEGORY_MY_DATA,
  EVENT_MY_DATA_ACTION_MORE_INFORMATION,
} from "../../../constants/analytics";
import { UserState } from "../../../store/reducers/user-reducer";
import { UtilsHome } from "../../../utils/UtilsHome";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { useIntersectionObserver } from "../../../hooks/intersection-hooks";
import { useWindowSize } from "../../../hooks/window-hooks";
import { UsageGuide } from "../../auth/MyData/UsageGuide/UsageGuide";
import { LinkButton, TTSButton } from "../../../components/__common/_controls";

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile, isTabletPortrait } = useWindowSize();

  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );

  const [sustainabilityLink, setSustainabilityLink] = useState<string>("");
  const [isGuideOpen, setGuideOpen] = useState<boolean>(false);

  const bodyRef = useRef<any>(null);
  const bannerImageRef = useRef<any>(null);
  const transparencyImageRef = useRef<any>(null);
  const choiceImageRef = useRef<any>(null);
  const safetyImageRef = useRef<any>(null);

  const options = { rootMargin: "0px", threshold: 1 };
  const bannerImageVisible = useIntersectionObserver(
    bannerImageRef,
    options,
    true
  );
  const transparencyImageVisible = useIntersectionObserver(
    transparencyImageRef,
    options,
    true
  );
  const choiceImageVisible = useIntersectionObserver(
    choiceImageRef,
    options,
    true
  );
  const safetyImageVisible = useIntersectionObserver(
    safetyImageRef,
    options,
    true
  );

  const adjustTopBanner = () => {
    const fixedBanner = document.getElementById("home-top-banner-fixed");
    const body = document.getElementById("home-body");
    const fixedBannerHeight = fixedBanner?.offsetHeight;

    if (isMobile) {
      body!.style.setProperty(
        "margin-top",
        `max(473px, calc(${String(fixedBannerHeight)}px - 57px))`
      );
    } else if (isTabletPortrait) {
      body!.style.setProperty(
        "margin-top",
        `max(637px, calc(${String(fixedBannerHeight)}px - 82px))`
      );
    } else {
      body!.style.setProperty(
        "margin-top",
        `max(746px, calc(${String(fixedBannerHeight)}px - 82px))`
      );
    }
  };

  useEffect(() => {
    let link: string = UtilsHome.getSustainabilityLink(
      layoutState.countryCode,
      layoutState.languageCode
    );

    if (!userState.isGeoIPLoading) {
      link = UtilsHome.getSustainabilityLink(
        userState.userCountryCode,
        layoutState.languageCode
      );
    }

    setSustainabilityLink(link);
  }, [
    userState.userCountryCode,
    layoutState.languageCode,
    userState.isGeoIPLoading,
    layoutState.countryCode,
  ]);

  useEffect(() => {
    adjustTopBanner();

    const onMouseScroll = () => {
      const container = document.getElementById("home-top-banner");
      container!.style.pointerEvents = "none";
    };

    const onTouchMove = () => {
      const container = document.getElementById("home-top-banner");
      container!.style.pointerEvents = "none";
    };

    const onMouseMove = () => {
      const container = document.getElementById("home-top-banner");
      container!.style.pointerEvents = "all";
    };

    document
      .getElementById("main-body")
      ?.addEventListener("mousemove", onMouseMove);

    document
      .getElementById("main-body")
      ?.addEventListener("wheel", onMouseScroll);

    document
      .getElementById("main-body")
      ?.addEventListener("touchmove", onTouchMove);

    document
      .getElementById("main-body")
      ?.addEventListener("resize", adjustTopBanner);

    return () => {
      document
        .getElementById("main-body")
        ?.removeEventListener("wheel", onMouseScroll);

      document
        .getElementById("main-body")
        ?.removeEventListener("mousemove", onMouseMove);

      document
        .getElementById("main-body")
        ?.removeEventListener("touchmove", onTouchMove);

      document
        .getElementById("main-body")
        ?.removeEventListener("resize", adjustTopBanner);
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    adjustTopBanner();
  });

  return (
    <>
      <div className="home-top-banner" id="home-top-banner">
        <div className="home-top-banner__fixed" id="home-top-banner-fixed">
          <Container>
            <Container className="home-top-banner__title">
              <Typography
                className="home-top-banner__title__text"
                variant="h1"
                component="h1"
              >
                {t("PRIVACY_HOME_HEADER_BANNER_TEXT1")}
                <TTSButton
                  value={
                    t("PRIVACY_HOME_HEADER_BANNER_TEXT1") +
                    " " +
                    t("PRIVACY_HOME_HEADER_BANNER_TEXT2") +
                    ". " +
                    t("PRIVACY_HOME_PRIVACY_BANNER_DESC")
                  }
                />
              </Typography>
              <Typography
                className="home-top-banner__title__text"
                variant="h1"
                component="h1"
              >
                {t("PRIVACY_HOME_HEADER_BANNER_TEXT2")}
              </Typography>
            </Container>
            <Container className="home-top-banner__caption">
              <Typography
                className="home-top-banner__caption__text"
                variant="body1"
              >
                {t("PRIVACY_HOME_PRIVACY_BANNER_DESC")}
              </Typography>
            </Container>
            <div className="home-top-banner__image" ref={bannerImageRef}>
              <Container className="motion">
                <img
                  className="motion-base"
                  src={
                    isMobile
                      ? SVG_ICONS.HOME_TOP_BANNER_BASE_MOBILE
                      : SVG_ICONS.HOME_TOP_BANNER_BASE
                  }
                  alt=""
                  onLoad={adjustTopBanner}
                />
                <Svg
                  className={`motion-magnifyingglass ${
                    bannerImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TOP_BANNER_MAGNIFYINGGLASS}
                />
                <Svg
                  className="motion-hand"
                  icon={SVG_ICONS.HOME_TOP_BANNER_HAND}
                />
                <Svg
                  className={`motion-laptop ${
                    bannerImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TOP_BANNER_LAPTOP}
                />
                <Svg
                  className={`motion-setting ${
                    bannerImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TOP_BANNER_SETTING}
                />
                <Svg
                  className={`motion-lock ${
                    bannerImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TOP_BANNER_LOCK}
                />
                <Svg
                  className={`motion-privacy ${
                    bannerImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TOP_BANNER_PRIVACY}
                />
              </Container>
              {/* <Svg
                className="home-top-banner__icon-arrow"
                icon={SVG_ICONS.ARROW_DOWN}
                width={40}
                height={40}
                onClick={() =>
                  bodyRef.current.scrollIntoView({ behavior: "smooth" })
                }
              /> */}
            </div>
          </Container>
        </div>
      </div>
      <div className="home-body" id="home-body" ref={bodyRef}>
        <div className="home-body__contents">
          {isGuideOpen && (
            <UsageGuide
              open={isGuideOpen}
              onClickClose={() => setGuideOpen(false)}
            />
          )}
          <div className="home-body__title-section">
            <Typography
              variant="h1"
              component="h1"
              className="home-body__title"
            >
              {isMobile
                ? t("PRIVACY_HOME_SAMSUNG_PRIVACY POLICY_MOBILE")
                : t("PRIVACY_HOME_SAMSUNG_PRIVACY POLICY")}
            </Typography>
            <LinkButton
              to="#"
              title={t("PRIVACY_MYDATA_PAGE_BTN_GUIDE")}
              iconPosition="left"
              icon={SVG_ICONS.GUIDE}
              underlined={false}
              iconSize={24}
              size="lg"
              textClassName={"home-body__info"}
              onClick={() => {
                triggerGAEvent(
                  EVENT_CATEGORY_MY_DATA,
                  EVENT_MY_DATA_ACTION_MORE_INFORMATION
                );
                setGuideOpen(true);
              }}
            />
          </div>
          <Row className="home-body__row home-body__row__reverse">
            <Col
              sm={12}
              md={6}
              className="home-body__col  home-body__col__left-caption"
            >
              <Typography variant="h2" className="home-body__col__title">
                {t("PRIVACY_HOME_BODY_INTRO_TITLE_TRANSPARENCY")}
              </Typography>
              <Typography variant="body1" className="home-body__col__desc">
                {t("PRIVACY_HOME_BODY_INTRO_EXP_TRANSPARENCY")}
              </Typography>
              <Typography variant="body1" className="home-body__col__desc-link">
                <Link
                  onClick={() =>
                    triggerGAEvent(
                      EVENT_CATEGORY_HOME_PAGE,
                      EVENT_HOME_ACTION_LEARN_HOW
                    )
                  }
                  to={ROUTES.MYDATA}
                  className="home-body__col__desc"
                >
                  {t("PRIVACY_HOME_LINK_LEARN_HOW")}
                </Link>
              </Typography>
            </Col>
            <Col sm={12} md={6} className="home-body__col">
              <div className="motion" ref={transparencyImageRef}>
                <img
                  className="home-body__col__icon"
                  src={SVG_ICONS.HOME_TRANSPARENCY_BASE}
                  alt=""
                />
                <Svg
                  className={`motion-transparency-icon-left ${
                    transparencyImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TRANSPARENCY_ICON1}
                />
                <Svg
                  className={`motion-transparency-icon-top ${
                    transparencyImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TRANSPARENCY_ICON2}
                />
                <Svg
                  className={`motion-transparency-icon-right ${
                    transparencyImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_TRANSPARENCY_ICON3}
                />
              </div>
            </Col>
          </Row>
          <Row className="home-body__row">
            <Col sm={12} md={6} className="home-body__col">
              <div className="motion" ref={choiceImageRef}>
                <img
                  className="home-body__col__icon"
                  src={SVG_ICONS.HOME_CHOICE_BASE}
                  alt=""
                />
                <Svg
                  className={`motion-choice-icon-left ${
                    choiceImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_CHOICE_ICON1}
                />
                <Svg
                  className={`motion-choice-icon-top ${
                    choiceImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_CHOICE_ICON2}
                />
                <Svg
                  className={`motion-choice-icon-right ${
                    choiceImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_CHOICE_ICON3}
                />
              </div>
            </Col>
            <Col
              sm={12}
              md={6}
              className="home-body__col home-body__col__right-caption"
            >
              <Typography variant="h2" className="home-body__col__title">
                {t("PRIVACY_HOME_BODY_INTRO_TITLE_CHOICE")}
              </Typography>
              <Typography variant="body1" className="home-body__col__desc">
                {t("PRIVACY_HOME_BODY_INTRO_EXP_CHOICE")}
              </Typography>
              <Typography variant="body1" className="home-body__col__desc-link">
                <Link
                  onClick={() =>
                    triggerGAEvent(
                      EVENT_CATEGORY_HOME_PAGE,
                      EVENT_HOME_ACTION_SEE_HOW
                    )
                  }
                  to={ROUTES.MYDATA}
                  className="home-body__col__desc"
                >
                  {t("PRIVACY_HOME_LINK_SEE_HOW")}
                </Link>
              </Typography>
            </Col>
          </Row>
          <Row className="home-body__row home-body__row__reverse">
            <Col
              sm={12}
              md={6}
              className="home-body__col  home-body__col__left-caption"
            >
              <Typography variant="h2" className="home-body__col__title">
                {t("PRIVACY_HOME_BODY_INTRO_TITLE_SAFETY")}
              </Typography>
              <Typography variant="body1" className="home-body__col__desc">
                {t("PRIVACY_HOME_BODY_INTRO_EXP_SAFETY")}
              </Typography>
              <Typography variant="body1" className="home-body__col__desc">
                <Trans
                  i18nKey="PRIVACY_HOME_LINK_CLICK_HERE_GET_STARTED"
                  components={{
                    a: (
                      <Link
                        onClick={() =>
                          triggerGAEvent(
                            EVENT_CATEGORY_HOME_PAGE,
                            EVENT_HOME_ACTION_CLICK_HERE
                          )
                        }
                        to={ROUTES.MYDATA}
                        className="home-body__col__desc-link"
                      />
                    ),
                  }}
                />
              </Typography>
            </Col>
            <Col sm={12} md={6} className="home-body__col">
              <div className="motion" ref={safetyImageRef}>
                <img
                  className="home-body__col__icon"
                  src={SVG_ICONS.HOME_SAFETY_BASE}
                  alt=""
                />
                <Svg
                  className={`motion-safety-icon-left ${
                    safetyImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_SAFETY_ICON1}
                />
                <Svg
                  className={`motion-safety-icon-top ${
                    safetyImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_SAFETY_ICON2}
                />
                <Svg
                  className={`motion-safety-icon-right ${
                    safetyImageVisible ? "animate" : ""
                  }`}
                  icon={SVG_ICONS.HOME_SAFETY_ICON3}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <div className="home-bottom-banner">
        <Container>
          <Typography
            variant="h3"
            component="h3"
            className="home-bottom-banner__title"
          >
            {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_TITLE")}
          </Typography>
          <Container>
            <Row className="home-bottom-banner__row">
              {userState.userData.countryCode !== "JPN" &&
              layoutState.countryCode !== "JP" ? (
                <>
                  <Col lg={4} md={12} sm={12}>
                    <Link
                      to={ROUTES.ABOUT_GRPS}
                      className="home-bottom-banner__col"
                      onClick={() =>
                        triggerGAEvent(
                          EVENT_CATEGORY_HOME_PAGE,
                          EVENT_HOME_ACTION_GPS
                        )
                      }
                    >
                      <Svg
                        className="home-bottom-banner__col__icon"
                        icon={SVG_ICONS.RIGHTS_RESPONSE}
                        width={96}
                        height={96}
                      />
                      <Typography
                        variant="title2"
                        className="home-bottom-banner__col__title"
                      >
                        {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_GRRS")}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="home-bottom-banner__col__caption"
                      >
                        {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_GRRS_TEXT")}
                      </Typography>
                    </Link>
                  </Col>
                  {sustainabilityLink !== "" && (
                    <Col lg={4} md={12} sm={12}>
                      <a
                        href={sustainabilityLink}
                        rel="noreferrer"
                        target="_blank"
                        className="home-bottom-banner__col"
                        onClick={() =>
                          triggerGAEvent(
                            EVENT_CATEGORY_HOME_PAGE,
                            EVENT_HOME_ACTION_SECURITY_PRIVACY
                          )
                        }
                      >
                        <Svg
                          className="home-bottom-banner__col__icon"
                          icon={SVG_ICONS.ABOUT_PRIVACY}
                          width={96}
                          height={96}
                        />
                        <Typography
                          variant="title2"
                          className="home-bottom-banner__col__title"
                        >
                          {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_ABOUT_PRIVACY")}
                        </Typography>
                        <Typography
                          variant="body2"
                          className="home-bottom-banner__col__caption"
                        >
                          {t(
                            "PRIVACY_HOME_BODY_DATA_MANAGEMENT_ABOUT_PRIVACY_TEXT"
                          )}
                        </Typography>
                      </a>
                    </Col>
                  )}

                  <Col lg={4} md={12} sm={12}>
                    <Link
                      to={ROUTES.MYDATA}
                      className="home-bottom-banner__col"
                      onClick={() =>
                        triggerGAEvent(
                          EVENT_CATEGORY_HOME_PAGE,
                          EVENT_HOME_ACTION_MANAGE_DATA
                        )
                      }
                    >
                      <Svg
                        className="home-bottom-banner__col__icon"
                        icon={SVG_ICONS.FUNCTION_GUIDE}
                        width={96}
                        height={96}
                      />
                      <Typography
                        variant="title2"
                        className="home-bottom-banner__col__title"
                      >
                        {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_MANAGE_DATA")}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="home-bottom-banner__col__caption"
                      >
                        {t(
                          "PRIVACY_HOME_BODY_DATA_MANAGEMENT_MANAGE_DATA_TEXT"
                        )}
                      </Typography>
                    </Link>
                  </Col>
                </>
              ) : (
                <>
                  <Col lg={6} md={12} sm={12}>
                    <Link
                      to={ROUTES.ABOUT_GRPS}
                      className="home-bottom-banner__col"
                      onClick={() =>
                        triggerGAEvent(
                          EVENT_CATEGORY_HOME_PAGE,
                          EVENT_HOME_ACTION_GPS
                        )
                      }
                    >
                      <Svg
                        className="home-bottom-banner__col__icon"
                        icon={SVG_ICONS.RIGHTS_RESPONSE}
                        width={96}
                        height={96}
                      />
                      <Typography
                        variant="title2"
                        className="home-bottom-banner__col__title"
                      >
                        {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_GRRS")}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="home-bottom-banner__col__caption"
                      >
                        {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_GRRS_TEXT")}
                      </Typography>
                    </Link>
                  </Col>

                  <Col lg={6} md={12} sm={12}>
                    <Link
                      to={ROUTES.MYDATA}
                      className="home-bottom-banner__col"
                      onClick={() =>
                        triggerGAEvent(
                          EVENT_CATEGORY_HOME_PAGE,
                          EVENT_HOME_ACTION_MANAGE_DATA
                        )
                      }
                    >
                      <Svg
                        className="home-bottom-banner__col__icon"
                        icon={SVG_ICONS.FUNCTION_GUIDE}
                        width={96}
                        height={96}
                      />
                      <Typography
                        variant="title2"
                        className="home-bottom-banner__col__title"
                      >
                        {t("PRIVACY_HOME_BODY_DATA_MANAGEMENT_MANAGE_DATA")}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="home-bottom-banner__col__caption"
                      >
                        {t(
                          "PRIVACY_HOME_BODY_DATA_MANAGEMENT_MANAGE_DATA_TEXT"
                        )}
                      </Typography>
                    </Link>
                  </Col>
                </>
              )}
            </Row>
          </Container>
        </Container>
      </div>
    </>
  );
};

export default HomePage;
